import { useQuery } from "@tanstack/react-query";

import { funnelsKey, getFunnels, type FreshnessMeta } from "./api";
import { DEFAULT_DAYS } from "./controls";
import { AppLaunchTab } from "./AppLaunchTab";
import { EmailTab } from "./EmailTab";
import { FunnelsTab } from "./FunnelsTab";
import { JourneyTab } from "./JourneyTab";
import { OnboardingTab } from "./OnboardingTab";
import { OutcomesTab } from "./OutcomesTab";
import { OverviewTab } from "./OverviewTab";
import { QrCodesTab } from "./QrCodesTab";
import { QrScansTab } from "./QrScansTab";
import { SourcesTab } from "./SourcesTab";
import { SplitTestTab } from "./SplitTestTab";
import { SummaryTab } from "./SummaryTab";

/**
 * The freshness bridge (native-module contract step 2). It reads the 30-day
 * funnels list under the same query key the Overview tab uses at its default
 * window, so at 30 days the bridge and the tab are one cache entry. At 7 or 90
 * days the tab reads its own window and this stays on 30: `data_as_of` is the
 * same for every window, so one extra cached read is the whole cost, and the
 * spec fixes the meta source at 30. `enabled` rather than a conditional call:
 * `useNativeModuleMeta` calls this on every render for every module.
 */
export function useLandingPagesMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const head = useQuery({
    queryKey: funnelsKey(DEFAULT_DAYS),
    queryFn: () => getFunnels(DEFAULT_DAYS),
    enabled,
  });
  return enabled ? head.data?.meta : undefined;
}

/**
 * Landing Pages, rendered natively in the portal. `decision` is deliberately
 * not read: the shell has already wrapped this in `PermissionGate` (I-7). An
 * unrecognised tab falls back to Overview rather than a blank viewport.
 */
export function LandingPagesModule({
  activeTabKey,
  areaName,
}: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  if (activeTabKey === "summary") return <SummaryTab areaName={areaName} />;
  if (activeTabKey === "journey") return <JourneyTab areaName={areaName} />;
  if (activeTabKey === "overview") return <OverviewTab areaName={areaName} />;
  if (activeTabKey === "funnels") return <FunnelsTab areaName={areaName} />;
  if (activeTabKey === "sources") return <SourcesTab areaName={areaName} />;
  if (activeTabKey === "email") return <EmailTab areaName={areaName} />;
  if (activeTabKey === "outcomes") return <OutcomesTab areaName={areaName} />;
  if (activeTabKey === "onboarding") return <OnboardingTab areaName={areaName} />;
  if (activeTabKey === "app_launch") return <AppLaunchTab areaName={areaName} />;
  if (activeTabKey === "qr_codes") return <QrCodesTab areaName={areaName} />;
  if (activeTabKey === "qr_scans") return <QrScansTab areaName={areaName} />;
  if (activeTabKey === "split_test") return <SplitTestTab areaName={areaName} />;
  return <SummaryTab areaName={areaName} />;
}
